<template>
  <v-container fluid>
    <!-- BRAND DETAILS ---------------------------------------------------->
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left small>mdi-domain</v-icon> Brand Details
        <v-spacer />
        <v-btn icon @click="brandOpen = !brandOpen">
          <v-icon>{{
            brandOpen ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-expand-transition>
        <div v-show="brandOpen">
          <v-card-text>
            <v-form ref="brandForm" lazy-validation>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="brand.brand_name"
                    label="Brand Name"
                    :rules="[rules.required]"
                    outlined
                    dense
                    placeholder="Enter your brand name"
                    hint="This name will represent your overall brand across all locations and platforms."
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="brand.businessName"
                    label="Location Naming Convention"
                    :rules="[rules.required]"
                    outlined
                    dense
                    :hint="'Define a consistent naming structure for your individual locations. Use variables like {{brandName}} for your brand and {{locality}} for the specific city/area. This helps maintain brand consistency while differentiating locations.'"
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12">
                  <v-card outlined class="pa-3 mb-4" light>
                    <div class="subtitle-2 font-weight-bold mb-2">
                      <v-icon small color="primary" class="mr-1"
                        >mdi-lightbulb-outline</v-icon
                      >
                      Suggested Dynamic Location Names
                    </div>
                    <v-chip-group>
                      <v-chip
                        v-for="(suggestion, i) in businessNameSuggestions"
                        :key="i"
                        small
                        outlined
                        @click="brand.businessName = suggestion"
                        class="mr-2 mb-1"
                      >
                        {{ suggestion }}
                      </v-chip>
                    </v-chip-group>
                    <div class="caption mt-2 grey--text">
                      <v-icon x-small class="mr-1"
                        >mdi-information-outline</v-icon
                      >
                      Click any suggestion to use it as your location name
                      template
                    </div>
                    <div
                      v-if="brand.businessName"
                      class="mt-3 pa-2 grey lighten-4 rounded"
                    >
                      <div class="caption text--secondary mb-1">Preview:</div>
                      <div class="body-2">
                        {{ formattedBusinessNamePreview }}
                      </div>
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="brand.website"
                    label="Website"
                    :rules="[rules.required, rules.url]"
                    prepend-inner-icon="mdi-web"
                    outlined
                    dense
                    hint="The official website for your brand. This will be used as the primary web presence for your business listings."
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="brand.primaryCategory"
                    :items="categories"
                    item-text="displayName"
                    item-value="name"
                    label="Primary Category"
                    :rules="[rules.required]"
                    hint="Select the main category that best describes your overall business. This is crucial for local search visibility."
                    persistent-hint
                    outlined
                    dense
                    placeholder="Select primary business category"
                    class="text-gray-700"
                    :loading="isCategoriesLoading"
                    :disabled="isCategoriesLoading"
                    :no-data-text="
                      isCategoriesLoading
                        ? 'Loading categories...'
                        : 'No categories found'
                    "
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="brand.additionalCategories"
                    :items="categories"
                    item-text="displayName"
                    item-value="name"
                    label="Additional Categories"
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    placeholder="Select up to 9 additional categories"
                    hint="Select up to 9 additional categories that describe other services or aspects of your business. This helps customers find you for more specific needs."
                    persistent-hint
                    outlined
                    dense
                    @change="enforceMaxCategories"
                    :rules="[rules.maxCategories]"
                    :loading="isCategoriesLoading"
                    :disabled="isCategoriesLoading"
                    :no-data-text="
                      isCategoriesLoading
                        ? 'Loading categories...'
                        : 'No categories found'
                    "
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        v-if="index < 5"
                        small
                        close
                        @click:close="removeCategory(item.name)"
                        :disabled="isCategoriesLoading"
                      >
                        {{ item.displayName || item }}
                      </v-chip>
                      <span v-if="index === 5" class="grey--text caption pl-2">
                        (+{{ brand.additionalCategories.length - 5 }} more)
                      </span>
                    </template>
                  </v-autocomplete>
                  <v-alert
                    v-if="brand.additionalCategories.length >= 9"
                    dense
                    type="info"
                    class="mt-2"
                    text
                  >
                    Maximum of 9 additional categories reached.
                  </v-alert>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="brand.description"
                    label="Business Description"
                    :rules="[rules.required]"
                    auto-grow
                    rows="3"
                    :counter="750"
                    outlined
                    placeholder="Describe your business, including services, unique selling points, and relevant information for customers"
                    hint="Provide a comprehensive description (up to 750 characters). Highlight what makes your business unique, your core services/products, and any other information helpful for customers. This description will be used across various online directories."
                    persistent-hint
                  />
                </v-col>

                <!-- SOCIAL MEDIA LINKS -->
                <v-col cols="12">
                  <v-card outlined class="pa-3 mb-4" light>
                    <div class="d-flex align-center justify-space-between mb-3">
                      <div class="subtitle-2 font-weight-bold">
                        <v-icon small color="primary" class="mr-1">mdi-share-variant</v-icon>
                        Social Media Links
                      </div>
                      <v-btn
                        v-if="hasAnySocialLinks"
                        small
                        text
                        color="error"
                        @click="clearSocialLinks"
                      >
                        <v-icon left x-small>mdi-delete</v-icon>
                        Clear All
                      </v-btn>
                    </div>
                    <div class="caption mb-3 grey--text">
                      Add your social media handles to help customers find and connect with your brand across different platforms.
                    </div>
                    
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.facebook"
                          label="Facebook"
                          prepend-inner-icon="mdi-facebook"
                          outlined
                          dense
                          placeholder="https://facebook.com/yourpage"
                          hint="Your Facebook page URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.instagram"
                          label="Instagram"
                          prepend-inner-icon="mdi-instagram"
                          outlined
                          dense
                          placeholder="https://instagram.com/yourhandle"
                          hint="Your Instagram profile URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.twitter"
                          label="Twitter/X"
                          prepend-inner-icon="mdi-twitter"
                          outlined
                          dense
                          placeholder="https://twitter.com/yourhandle"
                          hint="Your Twitter/X profile URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.youtube"
                          label="YouTube"
                          prepend-inner-icon="mdi-youtube"
                          outlined
                          dense
                          placeholder="https://youtube.com/@yourchannel"
                          hint="Your YouTube channel URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.threads"
                          label="Threads"
                          prepend-inner-icon="mdi-message-text"
                          outlined
                          dense
                          placeholder="https://threads.net/@yourhandle"
                          hint="Your Threads profile URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.linkedin"
                          label="LinkedIn"
                          prepend-inner-icon="mdi-linkedin"
                          outlined
                          dense
                          placeholder="https://linkedin.com/company/yourcompany"
                          hint="Your LinkedIn company page URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.tiktok"
                          label="TikTok"
                          prepend-inner-icon="mdi-music-note"
                          outlined
                          dense
                          placeholder="https://tiktok.com/@yourhandle"
                          hint="Your TikTok profile URL"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                      
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="brand.social_links.whatsapp"
                          label="WhatsApp"
                          prepend-inner-icon="mdi-whatsapp"
                          outlined
                          dense
                          placeholder="https://wa.me/1234567890"
                          hint="Your WhatsApp business link (e.g., https://wa.me/1234567890)"
                          persistent-hint
                          :rules="[rules.socialUrl]"
                        />
                      </v-col>
                    </v-row>
                    
                    <div class="mt-3 pa-2 grey lighten-4 rounded">
                      <div class="caption text--secondary mb-2">Social Media Links Preview:</div>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="(url, platform) in brand.social_links"
                          :key="platform"
                          v-if="url"
                          small
                          outlined
                          class="mr-2 mb-1"
                          :color="getSocialPlatformColor(platform)"
                        >
                          <v-icon left x-small>{{ getSocialPlatformIcon(platform) }}</v-icon>
                          {{ getSocialPlatformName(platform) }}
                        </v-chip>
                        <span v-if="!hasAnySocialLinks" class="caption grey--text">
                          No social media links added yet
                        </span>
                      </div>
                    </div>
                  </v-card>
                </v-col>

                <!-- PRODUCTS / SERVICES -->
              </v-row>
            </v-form>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- PRODUCTS & SERVICES ---------------------------------------------------->
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left small>mdi-cart-outline</v-icon> Products & Services
        <v-spacer />
        <v-btn icon @click="productsAndServicesOpen = !productsAndServicesOpen">
          <v-icon>{{
            productsAndServicesOpen ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-expand-transition>
        <div v-show="productsAndServicesOpen">
          <v-card-text>
            <v-btn
              color="primary"
              outlined
              @click="addProductService"
              class="mb-4"
            >
              <v-icon left>mdi-plus</v-icon> Add Product/Service
            </v-btn>

            <v-row
              v-for="(item, index) in brand.productsAndServices"
              :key="index"
            >
              <v-col cols="12">
                <v-card outlined class="pa-4 mb-3">
                  <v-row align="center">
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="item.name"
                        label="Name"
                        :rules="[rules.required]"
                        outlined
                        dense
                        placeholder="Product/Service Name"
                        hint="E.g., 'Espresso', 'Haircut', 'Consultation'"
                        persistent-hint
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-textarea
                        v-model="item.description"
                        label="Description"
                        auto-grow
                        rows="1"
                        outlined
                        dense
                        placeholder="Brief description"
                        hint="Optional: Describe the product or service."
                        persistent-hint
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="item.price"
                        label="Price"
                        outlined
                        dense
                        placeholder="e.g., $10 or Starting at $50"
                        hint="Optional: e.g., '$10' or 'Contact us'"
                        persistent-hint
                      />
                    </v-col>
                    <v-col cols="12" md="1" class="text-right">
                      <v-btn
                        icon
                        color="error"
                        @click="removeProductService(index)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <div
              v-if="
                !brand.productsAndServices ||
                brand.productsAndServices.length === 0
              "
              class="text-center grey--text py-4"
            >
              No products or services added yet. Click "Add Product/Service" to
              get started.
            </div>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Main Save Settings Button -->
    <div class="text-right mb-6">
      <v-btn color="primary" @click="saveSettings">Save Settings</v-btn>
    </div>

    <!-- POSTING FOOTER ---------------------------------------------------->
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left small>mdi-text-box-outline</v-icon> Posting Footer
        <v-spacer />
        <v-btn icon @click="postingFooterOpen = !postingFooterOpen">
          <v-icon>{{
            postingFooterOpen ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-expand-transition>
        <div v-show="postingFooterOpen">
          <v-card-text>
            <v-form ref="footerForm">
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    ref="footerTextarea"
                    v-model="brand.postingFooter"
                    label="Footer Text"
                    :rules="[
                      rules.required,
                      rules.maxWords,
                      rules.maxCharacters,
                    ]"
                    auto-grow
                    rows="2"
                    outlined
                    :counter="200"
                    placeholder="Enter your footer text (max 200 characters)"
                    :hint="footerHintText"
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12">
                  <v-card outlined class="pa-3" light>
                    <div class="subtitle-2 font-weight-bold mb-2">
                      <v-icon small color="primary" class="mr-1"
                        >mdi-variable</v-icon
                      >
                      Available Variables
                    </div>
                    <v-chip-group>
                      <v-chip
                        v-for="variable in footerVariables"
                        :key="variable"
                        small
                        outlined
                        @click="insertFooterVariable(variable)"
                        class="mr-2 mb-1"
                      >
                        {{ variable }}
                      </v-chip>
                    </v-chip-group>
                    <div class="caption mt-2 grey--text">
                      <v-icon x-small class="mr-1"
                        >mdi-information-outline</v-icon
                      >
                      Click any variable to insert it into your footer text
                    </div>
                    <div
                      v-if="brand.postingFooter"
                      class="mt-3 pa-2 grey lighten-4 rounded"
                    >
                      <div class="caption text--secondary mb-1">Preview:</div>
                      <div class="body-2 white-space-pre-wrap">
                        {{ formattedFooterPreview }}
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <!-- Separate Save Button for Posting Footer -->
          <v-card-actions class="justify-end pa-4">
            <v-btn color="primary" @click="savePostSettings">
              Save Post Settings
            </v-btn>
          </v-card-actions>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Navigation Section -->
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left small>mdi-cog</v-icon> Brand Configuration
        <v-spacer />
        <v-btn
          color="primary"
          outlined
          @click="$router.push('/store-locator-configurations')"
          class="ml-2"
        >
          <v-icon left small>mdi-map-marker</v-icon>
          Store Locator Settings
        </v-btn>
        <v-btn
          color="secondary"
          outlined
          @click="$router.push('/individual-store-configurations')"
          class="ml-2"
        >
          <v-icon left small>mdi-store</v-icon>
          Individual Store Settings
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p class="text-body-2 text-grey-600 mb-0">
          Configure your brand details and customize your store locator appearance and individual store page styling.
        </p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import {
  doc,
  updateDoc,
  getDoc,
  addDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils";

export default {
  name: "SettingsPage",
  data() {
    return {
      /* collapsible sections */
      brandOpen: true,
      listingOpen: true,
      keywordsOpen: true,
      productsAndServicesOpen: true,
      postingFooterOpen: true,

      /* Brand object (populate from workspace on mount) */
      brand: {
        brand_name: "",
        businessName: "",
        website: "",
        primaryCategory: null,
        additionalCategories: [],
        description: "",
        productsAndServices: [],
        postingFooter: "",
        social_links: {
          facebook: "",
          instagram: "",
          twitter: "",
          youtube: "",
          threads: "",
          linkedin: "",
          tiktok: "",
          whatsapp: "",
        },
      },

      /* Listing settings */
      listing: {
        profilePattern: "brand-location",
        staticText: "",
        autoSync: true,
      },
      profileOptions: [
        {
          value: "brand-location",
          preview: "<brand> <Location>",
        },
        {
          value: "brand-(location)",
          preview: "<brand> (<Location>)",
        },
        {
          value: "brand-static-location",
          preview: "<brand> <static text> | <Location>",
        },
      ],

      /* Business name suggestions */
      businessNameSuggestions: [
        "{{brandName}} {{locality}}",
        "{{brandName}} ({{locality}})",
        "{{brandName}} - {{locality}}",
      ],

      /* Keyword tracking */
      suggestedKeywords: [],
      selectedKeywords: [],

      /* Categories data */
      categories: [],
      categoriesId: [],

      /* Footer variables */
      footerVariables: [
        "{{brandName}}",
        "{{location-link}}",
        "{{location-short-address}}",
        "{{location-city}}",
      ],

      /* Hint texts */
      footerHintText:
        "Create a footer that will appear at the end of your posts. You can use variables like {{brandName}}, {{location-link}}, {{location-short-address}}, and {{location-city}}.",

      rules: {
        required: (v) => !!v || "Required",
        url: (v) => !v || /^https?:\/\/.+/.test(v) || "Invalid URL",
        socialUrl: (v) => {
          if (!v) return true; // Allow empty values
          const urlPattern = /^https?:\/\/(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;
          return urlPattern.test(v) || "Please enter a valid URL starting with http:// or https://";
        },
        maxCategories: (v) =>
          !v || v.length <= 9 || "Maximum 9 categories allowed",
        maxWords: (v) => {
          if (!v) return true;
          // Remove variables from word count
          const textWithoutVariables = v.replace(/\{\{[^}]+\}\}/g, "").trim();
          // Split by whitespace and filter out empty strings
          const words = textWithoutVariables
            .split(/\s+/)
            .filter((word) => word.length > 0);
          return (
            words.length <= 30 ||
            `Maximum 30 words allowed (currently using ${words.length} words, excluding variables)`
          );
        },
        maxCharacters: (v) => {
          if (!v) return true;
          return (
            v.length <= 200 ||
            `Maximum 200 characters allowed (currently using ${v.length} characters)`
          );
        },
      },
      maxAdditionalCategories: 9,
      isCategoriesLoading: true,
    };
  },
  computed: {
    formattedBusinessNamePreview() {
      // Example values
      const exampleBrandName = "Sunrise Coffee";
      const exampleLocality = "Manhattan";

      // Replace template variables with example values
      return this.brand.businessName
        .replace(/\{\{brandName\}\}/g, exampleBrandName)
        .replace(/\{\{locality\}\}/g, exampleLocality);
    },
    formattedFooterPreview() {
      if (!this.brand.postingFooter) {
        return "";
      }

      // Example values for preview
      const previewValues = {
        brandName: this.brand.brand_name || "Chitale Bandhu",
        "location-link": "https://g.page/your-business",
        "location-short-address": "Baner",
        "location-city": "Pune",
      };

      // Preserve original text and replace variables
      let preview = this.brand.postingFooter.trimStart(); // Remove leading whitespace

      // Replace all variables with their preview values while preserving formatting
      Object.entries(previewValues).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
        preview = preview.replace(regex, value);
      });

      return preview;
    },
    hasAnySocialLinks() {
      return Object.values(this.brand.social_links).some(url => url);
    },
  },
  async created() {
    // Fetch categories first
    await this.fetchGMBCategories();
    // Then fetch GMB data
    await this.fetchGMBData();
    // Finally fetch post settings
    await this.fetchPostSettings();
  },
  watch: {
    "brand.additionalCategories": {
      handler(newValue) {
        if (newValue && newValue.length > this.maxAdditionalCategories) {
          // Trim to max 9 categories if somehow more get added
          this.brand.additionalCategories = newValue.slice(
            0,
            this.maxAdditionalCategories
          );
          this.$snackbar.show("Maximum 9 additional categories allowed");
        }
      },
      deep: true,
    },
  },
  methods: {
    addProduct() {
      // This method seems to be a leftover, productsAndServices is used instead of products
      // If brand.products is still needed elsewhere, this can stay, otherwise consider removing
      // this.brand.products.push({ name: "", desc: "", price: "" });
    },
    addProductService() {
      this.brand.productsAndServices.push({
        name: "",
        description: "",
        price: "",
        // imageFile: null, // Removed
        // imagePreview: null // Removed
      });
    },
    removeProductService(index) {
      this.brand.productsAndServices.splice(index, 1);
    },
    async fetchGMBCategories() {
      try {
        // Try to get categories from sessionStorage instead of localStorage
        // sessionStorage is cleared when the browser session ends, but it's more appropriate for this data
        const cachedCategories = sessionStorage.getItem("gmb-categories");
        if (cachedCategories) {
          const { data, timestamp } = JSON.parse(cachedCategories);
          // Check if cache is less than 1 hour old
          if (Date.now() - timestamp < 60 * 60 * 1000) {
            this.categories = data;
            this.categoriesId = data.map((item) => item.name);
            this.isCategoriesLoading = false;
            return;
          }
        }

        const gmbCategories = await fetch(
          "https://firebasestorage.googleapis.com/v0/b/around-dev-1.appspot.com/o/gmb-categories%2Fgmb-categories.json?alt=media&token=cccd1123-19ac-4d68-a46a-56da6a0e481d"
        );
        const data = await gmbCategories.json();

        // Sort categories by displayName for faster lookup
        this.categories = data.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );
        this.categoriesId = this.categories.map((item) => item.name);

        try {
          // Only store essential data to reduce size
          const minimalCategories = this.categories.map((cat) => ({
            name: cat.name,
            displayName: cat.displayName,
          }));

          // Try to store in sessionStorage
          sessionStorage.setItem(
            "gmb-categories",
            JSON.stringify({
              data: minimalCategories,
              timestamp: Date.now(),
            })
          );
        } catch (storageError) {
          // If storage fails, just log it - the categories are still loaded in memory
          console.warn("Could not cache categories:", storageError);
        }
      } catch (error) {
        console.error("Error fetching GMB categories:", error);
        this.$snackbar.show(
          "Error loading categories. Please try refreshing the page."
        );
      } finally {
        this.isCategoriesLoading = false;
      }
    },
    suggestKeywords() {
      // very naive suggestion logic – replace with real AI / backend call
      const base = this.brand.businessName.split(" ")[0] || "";
      const cats = this.brand.additionalCategories.slice(0, 2).join(" ");
      const desc = this.brand.description;
      const seed = `${base} ${cats} ${desc}`.toLowerCase();
      const words = [...new Set(seed.split(/\W+/).filter((w) => w.length > 3))];
      this.suggestedKeywords = words.slice(0, 20);
    },
    toggleKeyword(kw) {
      const i = this.selectedKeywords.indexOf(kw);
      if (i >= 0) this.selectedKeywords.splice(i, 1);
      else if (this.selectedKeywords.length < 10)
        this.selectedKeywords.push(kw);
    },
    async fetchGMBData() {
      try {
        // Make sure we have the necessary IDs
        if (!this.currentUID || !this.WID) {
          console.warn("User ID or Workspace ID not available");
          return;
        }

        const gmbSettingsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-settings"
        );
        const latestDocQuery = query(
          gmbSettingsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(latestDocQuery);

        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const gmbData = latestDoc.data().gmb_details;

          this.brand.brand_name = gmbData.brand_name || "";
          this.brand.businessName =
            gmbData.naming || "{{brandName}} {{locality}}";
          this.brand.website = gmbData.website || "";
          this.brand.description = gmbData.summary || "";

          const fetchedProducts = gmbData.productsAndServices || [];
          this.brand.productsAndServices = fetchedProducts.map((p) => ({
            name: p.name || "",
            description: p.description || "",
            price: p.price || "",
          }));

          if (gmbData.categories && gmbData.categories.primaryCategory) {
            // Find the category in our loaded categories
            const primaryCat = this.categories.find(
              (cat) => cat.name === gmbData.categories.primaryCategory.name
            );
            this.brand.primaryCategory = primaryCat ? primaryCat.name : null;
          }

          if (gmbData.categories && gmbData.categories.additionalCategories) {
            this.brand.additionalCategories =
              gmbData.categories.additionalCategories
                .map((cat) => cat.name)
                .filter((name) => this.categoriesId.includes(name)); // Only include valid categories
          }

          // Load social links if they exist
          if (gmbData.social_links) {
            this.brand.social_links = {
              ...this.brand.social_links, // Keep defaults
              ...gmbData.social_links // Override with saved data
            };
          }
        } else {
          this.brand.brand_name = "";
          this.brand.businessName = "{{brandName}} {{locality}}";
          this.brand.website = "";
          this.brand.description = "";
          // this.brand.logoFile = null; // Removed
          // this.brand.logoPreview = null; // Removed
          this.brand.productsAndServices = [];
          // Keep default social_links structure
        }
      } catch (error) {
        console.error("Error fetching GMB data:", error);
        this.$snackbar.show(
          "Failed to load business settings. Please check console for details."
        );
      }
    },
    async fetchPostSettings() {
      try {
        const gmbPostSettingsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "post-setting"
        );

        const latestDocQuery = query(
          gmbPostSettingsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(latestDocQuery);

        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const postSettings = latestDoc.data();
          this.brand.postingFooter = postSettings.footer || "";
        }
      } catch (error) {
        console.error("Error fetching post settings:", error);
        this.$snackbar.show("Failed to load post settings");
      }
    },
    async fetchWorkspaceData() {
      try {
        const workspaceRef = doc(db, "users", this.currentUID, "fly_workspaces", this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const workspaceData = workspaceDoc.data();
          
          // Load social links from workspace if they exist
          if (workspaceData.social_links) {
            this.brand.social_links = {
              ...this.brand.social_links, // Keep defaults
              ...workspaceData.social_links // Override with workspace data
            };
          }
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
        this.$snackbar.show("Failed to load workspace data");
      }
    },
    async savePostSettings() {
      try {
        const valid = await this.$refs.footerForm.validate();
        if (!valid) {
          this.$snackbar.show(
            "Complete all required footer inputs within set limits."
          );
          return;
        }

        // Additional validation for character limit
        if (this.brand.postingFooter.length > 200) {
          this.$snackbar.show("Footer text exceeds 200 characters limit");
          return;
        }

        const gmbPostSettingsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "post-setting"
        );

        // Get the formatted preview text
        const formattedText = this.formattedFooterPreview;

        const postSettingsData = {
          footer: this.brand.postingFooter, // Save the original text with variables
          footerFormatted: formattedText, // Save the formatted preview version
          created_at: new Date(),
          variables: {
            brandName: this.brand.brand_name || "",
            "location-link": "",
            "location-short-address": "",
            "location-city": "",
            "location-area": "",
          },
        };

        await addDoc(gmbPostSettingsCollectionRef, postSettingsData);
        this.$snackbar.show("Post settings saved successfully");
      } catch (error) {
        console.error("Error saving post settings:", error);
        this.$snackbar.show("Failed to save post settings");
      }
    },
    async saveSettings() {
      try {
        const valid =
          (await this.$refs.brandForm.validate());
        if (!valid) {
          this.$snackbar.show("Please complete all required fields.");
          return;
        }
        const gmbSettingsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-settings"
        );

        const primaryCategoryObj = this.brand.primaryCategory
          ? {
              name: this.brand.primaryCategory,
              displayName:
                this.categories.find(
                  (cat) => cat.name === this.brand.primaryCategory
                )?.displayName || "",
            }
          : "";

        const additionalCategoriesObj = this.brand.additionalCategories.map(
          (catId) => {
            const category = this.categories.find((cat) => cat.name === catId);
            return {
              name: catId,
              displayName: category?.displayName || catId,
            };
          }
        );

        const settingsData = {
          gmb_details: {
            brand_name: this.brand.brand_name,
            naming: this.brand.businessName,
            categories: {
              primaryCategory: primaryCategoryObj,
              additionalCategories: additionalCategoriesObj,
            },
            website: this.brand.website,
            summary: this.brand.description,
            productsAndServices: this.brand.productsAndServices.map((p) => ({
              name: p.name,
              description: p.description,
              price: p.price,
            })),
            postingFooter: this.brand.postingFooter,
            social_links: this.brand.social_links,
          },
          created_at: new Date(),
        };

        // Removed Firebase Storage upload TODO comments

        await addDoc(gmbSettingsCollectionRef, settingsData);

        // Also save social links to the workspace document
        const workspaceRef = doc(db, "users", this.currentUID, "fly_workspaces", this.WID);
        await updateDoc(workspaceRef, {
          social_links: this.brand.social_links,
          updated_at: new Date(),
        });

        const recommendation_payload = {
          uid: this.currentUID,
          wid: "",
          type: "bulk",
          admin: this.WID,
        };
        const handleOnCallRecommendation = httpsCallable(
          functions,
          "gmbRetailRecommnederOnSettingUpdate"
        );
        handleOnCallRecommendation(recommendation_payload);

        this.$snackbar.show("Settings saved successfully");
      } catch (error) {
        console.error("Error saving settings:", error);
        this.$snackbar.show("Failed to save settings");
      }
    },
    removeCategory(categoryName) {
      const index = this.brand.additionalCategories.indexOf(categoryName);
      if (index >= 0) {
        this.brand.additionalCategories.splice(index, 1);
      }
    },
    enforceMaxCategories(categories) {
      if (categories.length > this.maxAdditionalCategories) {
        this.brand.additionalCategories = categories.slice(
          0,
          this.maxAdditionalCategories
        );
        this.$snackbar.show("Maximum 9 additional categories allowed");
      }
    },
    insertFooterVariable(variable) {
      const textareaRef = this.$refs.footerTextarea;

      // If ref or internal element doesn't exist, fallback
      if (!textareaRef || !textareaRef.$el) {
        this.brand.postingFooter += variable;
        return;
      }

      // Access native textarea inside Vuetify component
      const nativeTextarea = textareaRef.$el.querySelector("textarea");

      // Fallback in case it's not found
      if (!nativeTextarea) {
        this.brand.postingFooter += variable;
        return;
      }

      const cursorPos = nativeTextarea.selectionStart;
      const text = this.brand.postingFooter;

      // Insert variable at cursor position
      this.brand.postingFooter =
        text.substring(0, cursorPos) + variable + text.substring(cursorPos);

      // Move cursor to end of inserted variable
      this.$nextTick(() => {
        nativeTextarea.selectionStart = nativeTextarea.selectionEnd =
          cursorPos + variable.length;
        nativeTextarea.focus();
      });
    },
    getSocialPlatformColor(platform) {
      const colors = {
        facebook: "blue",
        instagram: "purple",
        twitter: "blue-darken-1",
        youtube: "red",
        threads: "grey-darken-1",
        linkedin: "blue-darken-2",
        tiktok: "black",
        whatsapp: "green",
      };
      return colors[platform] || "grey";
    },
    getSocialPlatformIcon(platform) {
      const icons = {
        facebook: "mdi-facebook",
        instagram: "mdi-instagram",
        twitter: "mdi-twitter",
        youtube: "mdi-youtube",
        threads: "mdi-message-text",
        linkedin: "mdi-linkedin",
        tiktok: "mdi-music-note",
        whatsapp: "mdi-whatsapp",
      };
      return icons[platform] || "mdi-link";
    },
    getSocialPlatformName(platform) {
      const names = {
        facebook: "Facebook",
        instagram: "Instagram",
        twitter: "Twitter",
        youtube: "YouTube",
        threads: "Threads",
        linkedin: "LinkedIn",
        tiktok: "TikTok",
        whatsapp: "WhatsApp",
      };
      return names[platform] || platform;
    },
    clearSocialLinks() {
      this.brand.social_links = {
        facebook: "",
        instagram: "",
        twitter: "",
        youtube: "",
        threads: "",
        linkedin: "",
        tiktok: "",
        whatsapp: "",
      };
    },
  },
};
</script>
<style scoped>
/* small helper so chips look clickable */
.v-chip {
  cursor: pointer;
}

/* .bordered-preview { // Removed as it's no longer needed
    border: 1px solid #e0e0e0; 
    border-radius: 4px;
    padding: 4px;
  } */

/* Fix for any red text issues using Tailwind */
:deep(.v-label),
:deep(.v-messages__message),
:deep(.v-input__slot > label),
:deep(.v-input__slot > .v-label),
:deep(.v-text-field__details) {
  @apply text-gray-700 !important;
}

/* Hint text fix for Vuetify with Tailwind */
:deep(.v-text-field__details),
:deep(.v-messages__wrapper),
:deep(.v-autocomplete__details) {
  @apply text-gray-500 !important;
}

/* Alternative approach if the above doesn't work */
:deep([class*="input"]) label,
:deep([class*="input"]) .v-messages__message,
:deep([class*="input"]) .v-input__slot > label,
:deep([class*="input"]) .v-label {
  @apply text-gray-700 !important;
}

/* Make sure hints are styled properly */
:deep(.v-messages__message) {
  @apply text-gray-500 !important;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}
</style>
